import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';
import { FormField } from '@shared/components/FormField/FormField';
import { PhoneInput, MultiSelect } from '@shared/components';
import { RolePermissionsDisplay } from './RolePermissionsDisplay';
import { useGames } from '../hooks/useGames';
import type { User, Role } from '../types';

interface EditUserDialogProps {
  open: boolean;
  user: User | null;
  roles: Role[];
  onClose: () => void;
  onSave: (data: { email?: string; name: string; phone: string; role_id: number; game_id: string }) => Promise<void>;
  onCreate?: (data: { email: string; name: string; phone: string; role_id: number; game_id: string }) => Promise<void>;
  isSaving: boolean;
}

export const EditUserDialog: React.FC<EditUserDialogProps> = ({
  open,
  user,
  roles,
  onClose,
  onSave,
  onCreate,
  isSaving,
}) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [roleId, setRoleId] = useState<number>(0);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedGameIds, setSelectedGameIds] = useState<number[]>([]);

  const { games } = useGames();

  // Convert games to MultiSelect options format
  const gameOptions = games.map((game) => ({
    label: game.game_name,
    value: game.game_id,
    optionId: game.game_id,
  }));

  const isEditMode = !!user;

  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setName(user.name);
      setPhone(user.phone || '');
      setRoleId(user.role_id);
      const role = roles.find((r) => r.role_id === user.role_id);
      setSelectedRole(role || null);
      // Parse comma-separated game_id string to array of numbers
      const gameIds = user.game_id ? user.game_id.split(',').map((id) => parseInt(id.trim(), 10)) : [];
      setSelectedGameIds(gameIds);
    } else {
      // Reset form for create mode
      setEmail('');
      setName('');
      setPhone('');
      setRoleId(0);
      setSelectedRole(null);
      setSelectedGameIds([]);
    }
  }, [user, roles, open]);

  const handleRoleChange = (newRoleId: number) => {
    setRoleId(newRoleId);
    const role = roles.find((r) => r.role_id === newRoleId);
    setSelectedRole(role || null);
  };

  const handleSave = async () => {
    // Convert selected game IDs array to comma-separated string
    const gameIdString = selectedGameIds.join(',');

    if (isEditMode) {
      await onSave({
        name,
        phone,
        role_id: roleId,
        game_id: gameIdString,
      });
    } else if (onCreate) {
      await onCreate({
        email,
        name,
        phone,
        role_id: roleId,
        game_id: gameIdString,
      });
    }
    onClose();
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  const isFormValid = isEditMode ? name && roleId : email && name && phone && roleId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
          {isEditMode ? 'Edit User' : 'Add New User'}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="span">
          {isEditMode ? 'Update user information and role assignment' : 'Create a new user account'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            gap: 4,
            mt: 2,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}
        >
          {/* LEFT SIDE → FORM FIELDS */}
          <Box sx={{ flex: 1, minWidth: '380px', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormField label="Full Name" tooltip="User's full name as it will appear in the system" required>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                placeholder="e.g., John Doe"
                variant="outlined"
              />
            </FormField>

            <FormField
              label="Email Address"
              tooltip={
                isEditMode ? 'Email address cannot be changed after account creation' : "User's email address for login"
              }
              required={!isEditMode}
            >
              <TextField
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                disabled={isEditMode}
                variant="outlined"
                placeholder={!isEditMode ? 'e.g., user@example.com' : undefined}
              />
            </FormField>

            <FormField
              label="Phone Number"
              tooltip="Enter phone number with country code (e.g., +91 9876543210)"
              required={!isEditMode}
            >
              <PhoneInput value={phone} onChange={setPhone} />
            </FormField>

            <FormField
              label="Assigned Role"
              tooltip="Select the role that determines user's access permissions"
              required
            >
              <TextField
                select
                value={roleId}
                onChange={(e) => handleRoleChange(Number(e.target.value))}
                fullWidth
                variant="outlined"
              >
                {roles.map((role) => (
                  <MenuItem key={role.role_id} value={role.role_id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {role.name}
                      {!role.is_active && (
                        <Typography variant="caption" color="text.secondary">
                          (Inactive)
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </FormField>

            <FormField
              label="Assigned Games"
              tooltip="Select the games this user can access"
            >
              <MultiSelect
                value={selectedGameIds}
                onChange={setSelectedGameIds}
                options={gameOptions}
                placeholder="Select games"
              />
            </FormField>
          </Box>

          {/* RIGHT SIDE → ROLE PREVIEW */}
          <Box
            sx={{
              flex: 1,
              minWidth: '380px',
              borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
              pl: 3,
            }}
          >
            {selectedRole ? (
              <RolePermissionsDisplay role={selectedRole} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 200,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Please select a role to view permissions
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || !isFormValid}
          startIcon={isSaving && <CircularProgress size={16} />}
        >
          {isEditMode ? 'Save' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
